const COLLECTIONS_STORAGE_KEY = 'asi:catalog-collections';
const COLLECTIONS_EVENT = 'asi:catalog-collections-updated';

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readCollections() {
  if (!isBrowser()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(COLLECTIONS_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed;
    }

    return [];
  } catch (error) {
    return [];
  }
}

function persistCollections(collections) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(COLLECTIONS_STORAGE_KEY, JSON.stringify(collections));

  if (typeof window.CustomEvent === 'function') {
    const event = new CustomEvent(COLLECTIONS_EVENT, { detail: { collections } });
    window.dispatchEvent(event);
    return;
  }

  const fallbackEvent = document.createEvent('Event');
  fallbackEvent.initEvent(COLLECTIONS_EVENT, false, false);
  fallbackEvent.detail = { collections };
  window.dispatchEvent(fallbackEvent);
}

function generateId(prefix = 'col') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function toTitleCase(label) {
  return String(label || '')
    .split(/\s+/)
    .map((word) => {
      if (!word) {
        return '';
      }
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ')
    .trim();
}

function normalizeTags(source) {
  if (!Array.isArray(source)) {
    return [];
  }
  return source
    .map((tag) => (tag && typeof tag === 'string' ? toTitleCase(tag.trim()) : ''))
    .filter(Boolean);
}

function normalizeArtworks(artworks) {
  if (!Array.isArray(artworks)) {
    return [];
  }

  return artworks
    .map((item, index) => {
      if (!item) {
        return null;
      }

      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (!trimmed) {
          return null;
        }
        return {
          id: generateId('art'),
          url: trimmed,
          title: `Artwork ${index + 1}`,
          tags: [],
        };
      }

      if (typeof item === 'object' && item.url) {
        const url = String(item.url).trim();
        if (!url) {
          return null;
        }
        const normalizedTags = normalizeTags(item.tags);
        return {
          id: item.id || generateId('art'),
          url,
          title: item.title || `Artwork ${index + 1}`,
          prompt: item.prompt || null,
          tags: normalizedTags,
        };
      }

      return null;
    })
    .filter(Boolean);
}

export function getAllCollections() {
  return readCollections();
}

export function getCollectionsForUser(userId) {
  if (!userId) {
    return [];
  }

  return readCollections().filter((collection) => collection.ownerId === userId);
}

export function getCollectionById(collectionId) {
  if (!collectionId) {
    return null;
  }

  return readCollections().find((collection) => collection.id === collectionId) || null;
}

export function createCollection({
  ownerId,
  title,
  curator,
  description,
  tags,
  artworks,
  visibility = 'private',
}) {
  if (!ownerId) {
    throw new Error('ownerId is required to create a collection.');
  }

  const trimmedTitle = (title || '').trim();
  if (!trimmedTitle) {
    throw new Error('A collection title is required.');
  }

  const normalizedTitle = toTitleCase(trimmedTitle);

  const normalizedArtworks = normalizeArtworks(artworks);
  if (!normalizedArtworks.length) {
    throw new Error('Add at least one artwork to create a collection.');
  }

  const now = new Date().toISOString();
  const collections = readCollections();
  const normalizedTags = normalizeTags(tags);

  const curatorLabel =
    (curator && typeof curator === 'string' && curator.trim()) || 'Workspace collection';

  const collectionId = generateId();
  const coverImage = normalizedArtworks[0];

  const normalizedVisibility =
    typeof visibility === 'string' && visibility.trim().toLowerCase() === 'public'
      ? 'public'
      : 'private';

  const newCollection = {
    id: collectionId,
    ownerId,
    title: normalizedTitle,
    description: (description || '').trim(),
    curator: curatorLabel,
    tags: normalizedTags,
    artworks: normalizedArtworks,
    coverImage,
    createdAt: now,
    updatedAt: now,
    visibility: normalizedVisibility,
  };

  collections.unshift(newCollection);
  persistCollections(collections);

  return newCollection;
}

export function updateCollection(collectionId, patch) {
  if (!collectionId) {
    throw new Error('collectionId is required to update a collection.');
  }

  const collections = readCollections();
  const index = collections.findIndex((collection) => collection.id === collectionId);

  if (index === -1) {
    return null;
  }

  const current = collections[index];
  const nextArtworks =
    patch && patch.artworks ? normalizeArtworks(patch.artworks) : current.artworks;

  const normalizedTitle =
    patch && patch.title ? toTitleCase(patch.title) : toTitleCase(current.title || '');

  const resolvedVisibility =
    patch && typeof patch.visibility === 'string'
      ? patch.visibility.trim().toLowerCase()
      : current.visibility || 'private';

  const normalizedVisibility = resolvedVisibility === 'public' ? 'public' : 'private';

  const updated = {
    ...current,
    ...patch,
    title: normalizedTitle,
    tags: normalizeTags(patch && Array.isArray(patch.tags) ? patch.tags : current.tags),
    artworks: nextArtworks,
    coverImage: (patch && patch.coverImage) || nextArtworks[0] || current.coverImage,
    updatedAt: new Date().toISOString(),
    visibility: normalizedVisibility,
  };

  collections[index] = updated;
  persistCollections(collections);
  return updated;
}

export function deleteCollection(collectionId) {
  if (!collectionId) {
    return false;
  }

  const collections = readCollections();
  const index = collections.findIndex((collection) => collection.id === collectionId);

  if (index === -1) {
    return false;
  }

  collections.splice(index, 1);
  persistCollections(collections);
  return true;
}

export function removeArtworksFromCollections({
  artworkIds = [],
  imageUrls = [],
} = {}) {
  const idSet = new Set(
    Array.isArray(artworkIds)
      ? artworkIds.map((id) => (id ? String(id).trim() : '')).filter(Boolean)
      : [],
  );
  const urlSet = new Set(
    Array.isArray(imageUrls)
      ? imageUrls.map((url) => (url ? String(url).trim() : '')).filter(Boolean)
      : [],
  );

  if (idSet.size === 0 && urlSet.size === 0) {
    return {
      removedArtworks: 0,
      removedCollections: 0,
    };
  }

  const collections = readCollections();
  if (!Array.isArray(collections) || collections.length === 0) {
    return {
      removedArtworks: 0,
      removedCollections: 0,
    };
  }

  let removedArtworks = 0;
  let removedCollections = 0;
  let hasChanges = false;

  const nextCollections = collections.reduce((accumulator, collection) => {
    if (!collection || !Array.isArray(collection.artworks)) {
      accumulator.push(collection);
      return accumulator;
    }

    const filteredArtworks = collection.artworks.filter((artwork) => {
      if (!artwork) {
        return false;
      }
      const matchesId = artwork.id && idSet.has(String(artwork.id));
      const matchesUrl = artwork.url && urlSet.has(String(artwork.url));
      if (matchesId || matchesUrl) {
        removedArtworks += 1;
        return false;
      }
      return true;
    });

    if (filteredArtworks.length === 0) {
      removedCollections += 1;
      hasChanges = true;
      return accumulator;
    }

    if (filteredArtworks.length !== collection.artworks.length) {
      hasChanges = true;
      const updatedCollection = {
        ...collection,
        artworks: filteredArtworks,
        coverImage: filteredArtworks[0] || null,
        updatedAt: new Date().toISOString(),
      };
      accumulator.push(updatedCollection);
      return accumulator;
    }

    accumulator.push(collection);
    return accumulator;
  }, []);

  if (hasChanges || removedCollections > 0) {
    persistCollections(nextCollections);
  }

  return {
    removedArtworks,
    removedCollections,
  };
}

export function subscribeToCollectionChanges(callback) {
  if (!isBrowser()) {
    return () => {};
  }

  const handler = (event) => {
    const detail = event && event.detail;
    const collections = (detail && detail.collections) || readCollections();
    callback(collections);
  };

  window.addEventListener(COLLECTIONS_EVENT, handler);

  return () => {
    window.removeEventListener(COLLECTIONS_EVENT, handler);
  };
}
